import { UserPasswordResetPayload } from '@/types/user';
import { RootState } from '@/app/store';
import camelizeNestedKeys from '@/shared/helpers/camelize';
import { clearUserAuthStorage } from '@/shared/helpers/storage';
import { UserData, UserMobile } from '@/types/user';
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { mainEmptySplitApi } from '@/features/main/mainApi';
import { ResponseRTK } from '@/types/rtkCustomTypes';

export type InfoResponse = {
  data: {
    user: Record<string, unknown> | null;
    business: Record<string, unknown> | null;
  };
};

export type InfoMobileResponse = {
  data: Record<string, unknown> | null;
};

export type TaxIdentification = {
  country: string;
  number: string;
};

export type TaxIdentificationsResponse = {
  collection: TaxIdentification[];
  per_page: number;
  total: number;
  current_page: number;
  page_total: number;
};

export type SignUpInput = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  passwordConfirmation: string;
};

export type CreateUser = {
  country_of_residence: string;
  data_policy_accepted: boolean;
  email: string;
  first_name: string;
  last_name: string;
  nationality: string;
  password: string;
  tos_accepted: boolean;
};

type UserSession = {
  id: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
  updated_at: string;
  is_current: boolean;
};

export type UserSessionsResponse = {
  data: UserSession[];
};

const userAdapter = createEntityAdapter<UserData>();
const initialState = userAdapter.getInitialState();

const userMobileAdapter = createEntityAdapter<UserMobile>();
const initialMobileState = userMobileAdapter.getInitialState();

export const userApi = mainEmptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getUser: builder.query({
      query: () => ({
        url: '/api/v1/users/current/info'
      }),
      transformResponse: (res: InfoResponse) => {
        const camelizedResponse = camelizeNestedKeys(res.data.user);
        return userAdapter.setOne(initialState, {
          id: 'user',
          ...camelizedResponse
        });
      }
    }),
    getUserCompany: builder.query({
      query: () => ({
        url: 'api/v1/users/current/info'
      }),
      transformResponse: (res: InfoResponse) => {
        const camelizedResponse = camelizeNestedKeys(res.data);
        return camelizedResponse;
      }
    }),
    creatUserMobile: builder.mutation({
      query(data) {
        return {
          url: '/api/v1/persons/current/mobile_numbers',
          method: 'POST',
          body: {
            ...data
          }
        };
      }
    }),
    getUserMobile: builder.query({
      query() {
        return {
          url: '/api/v1/persons/current/mobile_number'
        };
      },
      transformResponse: (result: InfoMobileResponse) => {
        return userMobileAdapter.setOne(initialMobileState, camelizeNestedKeys(result));
      }
    }),
    deletUserMobile: builder.mutation({
      query() {
        return {
          url: '/api/v1/persons/current/mobile_number',
          method: 'DELETE'
        };
      }
    }),
    authorizeUserMobile: builder.mutation({
      query() {
        return {
          url: '/api/v1/persons/current/mobile_number/authorize',
          method: 'POST'
        };
      }
    }),
    updateUser: builder.mutation({
      query(data) {
        return {
          url: '/api/v1/persons/current',
          method: 'PATCH',
          body: {
            ...data
          }
        };
      },
      invalidatesTags: ['User']
    }),
    updateConfirmation: builder.mutation({
      query(data) {
        return {
          url: '/api/v1/users/update_confirmation',
          method: 'POST',
          body: {
            tan: data.tan,
            change_request_id: data.changeRequestId
          }
        };
      }
    }),
    signUpUser: builder.mutation<unknown, CreateUser>({
      query(data) {
        return {
          url: '/api/v1/users/sign_up',
          method: 'POST',
          body: {
            user: {
              ...data
            }
          }
        };
      },
      invalidatesTags: ['User']
    }),
    logoutUser: builder.mutation<unknown, void>({
      query: () => ({
        url: '/api/v1/users/logout',
        method: 'POST'
      }),
      transformResponse: async () => {
        try {
          clearUserAuthStorage();
        } catch (error) {
          console.error('There is an issue with the logout', error);
        }

        return userAdapter.removeAll(initialState);
      }
    }),
    passwordReset: builder.mutation<ResponseRTK<unknown>, UserPasswordResetPayload>({
      query(data) {
        return {
          url: '/api/v1/users/password',
          method: 'POST',
          body: {
            ...data
          }
        };
      }
    }),
    getTaxIdentifications: builder.query({
      query() {
        return { url: '/api/v1/persons/current/tax_identifications' };
      },
      transformResponse: (res: TaxIdentificationsResponse) => camelizeNestedKeys(res)
    }),
    createTaxIdentification: builder.mutation({
      query(data) {
        return {
          url: '/api/v1/persons/current/tax_identifications',
          method: 'POST',
          body: {
            ...data
          }
        };
      }
    }),
    getUserSessions: builder.query({
      query() {
        return { url: '/api/v1/users/sessions' };
      },
      transformResponse: (res: UserSessionsResponse) => camelizeNestedKeys(res.data)
    }),
    deleteUserSession: builder.mutation({
      query(sessionID) {
        return {
          url: `/api/v1/users/sessions${sessionID ? `/${sessionID}` : ''}`,
          method: 'DELETE'
        };
      }
    })
  })
});

// @ts-ignore
const selectUserResult = userApi.endpoints.getUser.select();

export const selectUserData = createSelector(selectUserResult, (apiSlice) => apiSlice.data);

export const { selectAll: selectUser } = userAdapter.getSelectors<RootState>(
  (state) => selectUserData(state) ?? initialState
);

export const {
  useGetUserQuery,
  useGetUserCompanyQuery,
  useLogoutUserMutation,
  useUpdateUserMutation,
  useUpdateConfirmationMutation,
  useSignUpUserMutation,
  useGetUserMobileQuery,
  useDeletUserMobileMutation,
  useCreatUserMobileMutation,
  useAuthorizeUserMobileMutation,
  usePasswordResetMutation,
  useGetTaxIdentificationsQuery,
  useCreateTaxIdentificationMutation,
  useGetUserSessionsQuery,
  useDeleteUserSessionMutation
} = userApi;
